import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams, BrowserRouter } from 'react-router-dom';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { Hero } from '@/app/components/Hero';
import { PLP } from '@/app/components/pages/PLP';
import { PDP } from '@/app/components/pages/PDP';
import { Cart } from '@/app/components/pages/Cart';
import { Checkout } from '@/app/components/pages/Checkout';
import { Contact } from '@/app/components/pages/Contact';
import { SizeGuidePage } from '@/app/components/pages/SizeGuidePage';
import { CertificationPage } from '@/app/components/pages/CertificationPage';
import { TermsPage } from '@/app/components/pages/TermsPage';
import { PrivacyPage } from '@/app/components/pages/PrivacyPage';
import { ProductCard } from '@/app/components/ProductCard';
import { ComparisonSection } from '@/app/components/home/ComparisonSection';
import { RidingStyleSelector } from '@/app/components/home/RidingStyleSelector';
import { Product, PRODUCTS } from '@/app/data';
import { toast, Toaster } from 'sonner';
import { ArrowRight, Star, Quote } from 'lucide-react';

// Wrapper for PDP to handle URL params
const PDPWrapper = ({ 
  onAddToCart, 
  onBack 
}: { 
  onAddToCart: (product: Product) => void, 
  onBack: () => void 
}) => {
  const { id } = useParams<{ id: string }>();
  const product = PRODUCTS.find((p: Product) => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#061970] mb-4">Producto no encontrado</h2>
          <button 
            onClick={onBack}
            className="text-[#FF7F32] font-bold hover:underline"
          >
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <PDP 
      product={product} 
      onAddToCart={onAddToCart} 
      onBack={onBack} 
    />
  );
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [helmetTypeFilter, setHelmetTypeFilter] = useState<string | null>(null);

  // Sync current page state with location for Header highlighting
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') setCurrentPage('home');
    else if (path.startsWith('/products') || path.startsWith('/product')) setCurrentPage('plp');
    else if (path === '/cart') setCurrentPage('cart');
    else if (path === '/checkout') setCurrentPage('checkout');
    else if (path === '/garantia') setCurrentPage('contacto');
    else setCurrentPage('');
  }, [location]);

  // Navigation Handler (adapting to existing components)
  const handleNavigate = (page: string) => {
    switch (page) {
      case 'home':
        navigate('/');
        break;
      case 'plp':
        navigate('/products');
        break;
      case 'cart':
        navigate('/cart');
        break;
      case 'checkout':
        navigate('/checkout');
        break;
      case 'contact':
        navigate('/contacto');
        break;
      case 'pdp':
        navigate('/products');
        break;
      default:
        navigate('/');
    }
    window.scrollTo(0, 0);
  };

  const handleProductClick = (product: Product) => {
    navigate(`/product/${product.id}`);
    window.scrollTo(0, 0);
  };

  // Cart Logic
  const addToCart = (product: Product) => {
    setCartItems([...cartItems, product]);
    toast.success(`${product.name} agregado al equipo`);
  };

  const removeFromCart = (productId: string) => {
    setCartItems(cartItems.filter(item => item.id !== productId));
    toast.error("Producto eliminado");
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + item.price, 0);

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans selection:bg-[#FF7F32] selection:text-white">
      <Toaster position="top-center" richColors />
      
      <Header 
        onNavigate={handleNavigate} 
        currentPage={currentPage}
      />
      
      <main>
        <Routes>
          <Route path="/" element={
            <>
              <Hero onCtaClick={() => handleNavigate('plp')} />
              
              <RidingStyleSelector onSelect={(style: string) => {
                  // Map riding style to helmet type
                  const styleToType: Record<string, string> = {
                    'city': 'Jet',
                    'touring': 'Modular',
                    'sport': 'Integral',
                    'offroad': 'Cross'
                  };
                  const helmetType = styleToType[style] || null;
                  setHelmetTypeFilter(helmetType);
                  toast.success(`Filtrando por estilo: ${style.toUpperCase()}`);
                  handleNavigate('plp');
              }} />

              <ComparisonSection onCategorySelect={(category: string) => {
                  // Map category name to helmet type
                  const categoryToType: Record<string, string> = {
                    'JET / Abierto': 'Jet',
                    'Integral': 'Integral',
                    'Modular': 'Modular',
                    'Cross / Off-road': 'Cross'
                  };
                  const helmetType = categoryToType[category] || null;
                  setHelmetTypeFilter(helmetType);
                  toast.success(`Explorando categoría: ${category}`);
                  handleNavigate('plp');
              }} />

              {/* Featured Products - No Filters */}
              <div className="bg-[#F9FAFB] py-20">
                  <div className="container mx-auto px-4 text-center mb-12">
                      <h2 className="text-4xl font-black italic text-[#061970] uppercase tracking-tighter mb-4">Destacados</h2>
                      <p className="text-gray-500 max-w-2xl mx-auto">La mejor tecnología en protección para cada estilo de conducción.</p>
                  </div>
                  <div className="container mx-auto px-4">
                       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                          {PRODUCTS.slice(0, 3).map((product: Product) => (
                              <ProductCard 
                                  key={product.id} 
                                  product={product} 
                                  onAddToCart={addToCart}
                                  onClick={handleProductClick}
                              />
                          ))}
                       </div>
                       
                       <div className="mt-12 text-center">
                          <button 
                             onClick={() => handleNavigate('plp')}
                             className="inline-flex items-center gap-2 px-8 py-3 bg-[#FF7F32] text-white font-bold uppercase italic rounded-full shadow-lg hover:bg-[#e66a20] transition-colors group"
                          >
                             Ver Todo el Catálogo <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </button>
                       </div>
                  </div>
              </div>

              {/* Tech Banner Section */}
              <div className="relative h-[500px] flex items-center bg-gray-900 overflow-hidden">
                 <div className="absolute inset-0 bg-black/40 z-10"></div>
                 <img 
                    src="https://images.unsplash.com/photo-1711533865848-829b5b322d73?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb3RvcmN5Y2xlJTIwcmFjaW5nJTIwaGVsbWV0JTIwc2FmZXR5JTIwdGVjaG5vbG9neSUyMGNsb3NlJTIwdXB8ZW58MXx8fHwxNzY5NTQ5NzU1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral" 
                    alt="Technology" 
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                 />
                 <div className="container mx-auto px-4 relative z-20 text-center">
                    <span className="inline-block border border-white/30 backdrop-blur text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-6">
                        Seguridad Certificada
                    </span>
                    <h2 className="text-4xl md:text-6xl font-black italic text-white uppercase tracking-tighter mb-6 drop-shadow-lg">
                       Ingeniería de <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF7F32] to-[#459EDF]">Competición</span>
                    </h2>
                    <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                       Cada casco MRC supera los estándares internacionales de seguridad ECE 22.06 y DOT, diseñados para proteger lo que más importa.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 md:gap-12 text-white font-bold uppercase tracking-widest text-sm">
                       <div className="flex items-center gap-3 bg-black/30 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/10">
                          <span className="w-3 h-3 bg-[#FF7F32] rounded-full shadow-[0_0_10px_#FF7F32]"></span> Fibra de Carbono
                       </div>
                       <div className="flex items-center gap-3 bg-black/30 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/10">
                          <span className="w-3 h-3 bg-[#459EDF] rounded-full shadow-[0_0_10px_#459EDF]"></span> Visión 180°
                       </div>
                       <div className="flex items-center gap-3 bg-black/30 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/10">
                          <span className="w-3 h-3 bg-white rounded-full shadow-[0_0_10px_white]"></span> Aerodinámica
                       </div>
                    </div>
                 </div>
              </div>
              
              {/* Social Proof Section (User Reviews) */}
              <div className="bg-[#F9FAFB] py-24 border-b border-gray-200">
                <div className="container mx-auto px-4">
                  <div className="text-center mb-16">
                    <h2 className="text-4xl font-black italic text-[#061970] uppercase tracking-tighter mb-2">Pilotos Reales</h2>
                    <p className="text-gray-500 text-lg">La confianza se gana en el asfalto</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Review 1 */}
                    <div className="bg-[#061970] text-white p-8 rounded-2xl relative group hover:-translate-y-2 transition-transform duration-300 shadow-xl">
                      <Quote className="absolute top-6 right-6 w-10 h-10 text-[#FF7F32] opacity-20" />
                      <div className="flex gap-1 mb-6">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-5 h-5 text-[#FF7F32] fill-[#FF7F32]" />
                        ))}
                      </div>
                      <p className="text-xl font-medium italic leading-relaxed mb-6 border-l-4 border-[#FF7F32] pl-4">
                        "El interior es súper cómodo para viajes largos. Ya no siento fatiga en el cuello después de horas rodando."
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#459EDF] flex items-center justify-center font-bold text-lg">
                          JP
                        </div>
                        <div>
                          <p className="font-bold uppercase tracking-wider text-sm">Juan Pérez</p>
                          <p className="text-[#FF7F32] text-xs font-bold uppercase tracking-widest">Motociclista Diario</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 2 */}
                    <div className="bg-[#061970] text-white p-8 rounded-2xl relative group hover:-translate-y-2 transition-transform duration-300 shadow-xl">
                      <Quote className="absolute top-6 right-6 w-10 h-10 text-[#459EDF] opacity-20" />
                      <div className="flex gap-1 mb-6">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-5 h-5 text-[#FF7F32] fill-[#FF7F32]" />
                        ))}
                      </div>
                      <p className="text-xl font-medium italic leading-relaxed mb-6 border-l-4 border-[#459EDF] pl-4">
                        "La ventilación es increíble, incluso en verano. Trabajo todo el día en la moto y se nota la diferencia."
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#FF7F32] flex items-center justify-center font-bold text-lg">
                          MG
                        </div>
                        <div>
                          <p className="font-bold uppercase tracking-wider text-sm">María González</p>
                          <p className="text-[#459EDF] text-xs font-bold uppercase tracking-widest">Delivery Profesional</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 3 */}
                    <div className="bg-[#061970] text-white p-8 rounded-2xl relative group hover:-translate-y-2 transition-transform duration-300 shadow-xl">
                      <Quote className="absolute top-6 right-6 w-10 h-10 text-[#FF7F32] opacity-20" />
                      <div className="flex gap-1 mb-6">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-5 h-5 text-[#FF7F32] fill-[#FF7F32]" />
                        ))}
                      </div>
                      <p className="text-xl font-medium italic leading-relaxed mb-6 border-l-4 border-[#FF7F32] pl-4">
                        "Me siento mucho más seguro sabiendo que tiene certificación ECE 22.06. La calidad se siente al instante."
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#459EDF] flex items-center justify-center font-bold text-lg">
                          CR
                        </div>
                        <div>
                          <p className="font-bold uppercase tracking-wider text-sm">Carlos Rodríguez</p>
                          <p className="text-[#FF7F32] text-xs font-bold uppercase tracking-widest">Piloto Amateur</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          } />
          
          <Route path="/products" element={
            <PLP 
              onAddToCart={addToCart} 
              onProductClick={handleProductClick}
              initialTypeFilter={helmetTypeFilter}
              onClearTypeFilter={() => setHelmetTypeFilter(null)}
            />
          } />
          
          <Route path="/product/:id" element={
            <PDPWrapper 
              onAddToCart={addToCart} 
              onBack={() => handleNavigate('plp')} 
            />
          } />
          
          <Route path="/cart" element={
            <Cart 
              items={cartItems} 
              onRemove={removeFromCart} 
              onCheckout={() => handleNavigate('checkout')}
              onContinueShopping={() => handleNavigate('plp')}
            />
          } />
          
          <Route path="/checkout" element={
            <Checkout 
              total={cartTotal} 
              onBack={() => handleNavigate('cart')}
              onSuccess={() => {
                  toast.success("¡Pedido confirmado! Gracias por tu compra.");
                  setCartItems([]);
                  handleNavigate('home');
              }}
            />
          } />

          <Route path="/contacto" element={<Contact />} />
          <Route path="/guia-de-tallas" element={<SizeGuidePage />} />
          <Route path="/certificaciones" element={<CertificationPage />} />
          <Route path="/terminos" element={<TermsPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
